import {
  useMemo,
  useState
} from 'react';

import {
  submitStudentHonorsPoll
} from '../../utils/honorsPolls';

function cleanQuestions(
  poll
) {
  return (
    Array.isArray(
      poll?.questions
    )
      ? poll.questions
      : []
  );
}

function initialAnswers(
  questions
) {
  const values = {};

  questions.forEach(
    question => {
      if (
        question.questionType ===
          'multiple_choice' ||
        question.questionType ===
          'ranking'
      ) {
        values[
          question.questionId
        ] = [];
      } else {
        values[
          question.questionId
        ] = '';
      }
    }
  );

  return values;
}

function missingRequired(
  question,
  value
) {
  if (!question.required) {
    return false;
  }

  if (
    question.questionType ===
      'multiple_choice' ||
    question.questionType ===
      'ranking'
  ) {
    return !Array.isArray(value) ||
      value.length === 0;
  }

  return (
    String(
      value ?? ''
    ).trim() ===
    ''
  );
}

function RankingAnswer({
  question,
  value,
  onChange
}) {
  const ranked =
    Array.isArray(value)
      ? value
      : [];

  const remaining =
    (
      Array.isArray(
        question.options
      )
        ? question.options
        : []
    ).filter(
      option =>
        !ranked.includes(
          option
        )
    );

  function move(
    index,
    delta
  ) {
    const next =
      [...ranked];

    const target =
      index + delta;

    if (
      target < 0 ||
      target >=
        next.length
    ) {
      return;
    }

    [
      next[index],
      next[target]
    ] = [
      next[target],
      next[index]
    ];

    onChange(next);
  }

  return (
    <div className="hp-ranking">
      {ranked.length > 0 && (
        <div className="hp-ranked-list">
          {ranked.map(
            (option, index) => (
              <div
                className="hp-ranked-row"
                key={option}
              >
                <span className="hp-rank-number">
                  {index + 1}
                </span>

                <span className="hp-rank-text">
                  {option}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    move(
                      index,
                      -1
                    )
                  }
                  disabled={
                    index === 0
                  }
                  aria-label={
                    `Move ${option} up`
                  }
                >
                  ↑
                </button>

                <button
                  type="button"
                  onClick={() =>
                    move(
                      index,
                      1
                    )
                  }
                  disabled={
                    index ===
                      ranked.length - 1
                  }
                  aria-label={
                    `Move ${option} down`
                  }
                >
                  ↓
                </button>

                <button
                  type="button"
                  onClick={() =>
                    onChange(
                      ranked.filter(
                        item =>
                          item !==
                          option
                      )
                    )
                  }
                >
                  Remove
                </button>
              </div>
            )
          )}
        </div>
      )}

      {remaining.length > 0 && (
        <>
          <div className="hp-rank-help">
            Add as many choices as you
            want to rank.
          </div>

          <div className="hp-add-ranking">
            {remaining.map(
              option => (
                <button
                  type="button"
                  key={option}
                  onClick={() =>
                    onChange([
                      ...ranked,
                      option
                    ])
                  }
                >
                  + {option}
                </button>
              )
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function HonorsPollForm({
  session,
  poll,
  onSubmitted,
  onCancel,
  compact = false
}) {
  const questions =
    useMemo(
      () =>
        cleanQuestions(
          poll
        ),
      [poll]
    );

  const [
    answers,
    setAnswers
  ] =
    useState(
      () =>
        initialAnswers(
          questions
        )
    );

  const [
    submitting,
    setSubmitting
  ] =
    useState(false);

  const [
    error,
    setError
  ] =
    useState('');

  function setAnswer(
    questionId,
    value
  ) {
    setAnswers(
      current => ({
        ...current,
        [questionId]:
          value
      })
    );
  }

  async function submit(
    event
  ) {
    event.preventDefault();

    const missing =
      questions.find(
        question =>
          missingRequired(
            question,
            answers[
              question.questionId
            ]
          )
      );

    if (missing) {
      setError(
        `Please answer: ${missing.prompt}`
      );
      return;
    }

    if (
      !window.confirm(
        'Submit this Poll? Your responses cannot be changed after submission.'
      )
    ) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await submitStudentHonorsPoll(
        session,
        poll.pollId,
        answers
      );

      onSubmitted?.(
        poll.pollId
      );

    } catch (err) {
      setError(
        err?.message ||
        'The Poll could not be submitted.'
      );

    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className={
        compact
          ? 'hp-shell compact'
          : 'hp-shell'
      }
    >
      <style>{pollCss}</style>

      <div className="hp-heading">
        <div>
          <div className="hp-kicker">
            Tunghai Honors Program
          </div>

          <h2>
            {poll?.title ||
              'Poll'}
          </h2>

          {poll?.description && (
            <p>
              {poll.description}
            </p>
          )}
        </div>

        {onCancel && (
          <button
            type="button"
            className="hp-close"
            onClick={onCancel}
          >
            Back
          </button>
        )}
      </div>

      {error && (
        <div className="hp-error">
          {error}
        </div>
      )}

      <form
        onSubmit={submit}
      >
        {questions.map(
          (
            question,
            index
          ) => {
            const value =
              answers[
                question.questionId
              ];

            return (
              <section
                className="hp-question"
                key={
                  question.questionId
                }
              >
                <div className="hp-question-title">
                  <span>
                    {index + 1}.
                  </span>

                  <strong>
                    {question.prompt}
                  </strong>

                  {question.required && (
                    <em>
                      Required
                    </em>
                  )}
                </div>

                {question.questionType ===
                  'single_choice' && (
                  <div className="hp-options">
                    {(
                      question.options ||
                      []
                    ).map(
                      option => (
                        <label
                          key={option}
                        >
                          <input
                            type="radio"
                            name={
                              question.questionId
                            }
                            checked={
                              value ===
                              option
                            }
                            onChange={() =>
                              setAnswer(
                                question.questionId,
                                option
                              )
                            }
                          />

                          <span>
                            {option}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                )}

                {question.questionType ===
                  'multiple_choice' && (
                  <div className="hp-options">
                    {(
                      question.options ||
                      []
                    ).map(
                      option => {
                        const selected =
                          Array.isArray(
                            value
                          ) &&
                          value.includes(
                            option
                          );

                        return (
                          <label
                            key={
                              option
                            }
                          >
                            <input
                              type="checkbox"
                              checked={
                                selected
                              }
                              onChange={() => {
                                const current =
                                  Array.isArray(
                                    value
                                  )
                                    ? value
                                    : [];

                                setAnswer(
                                  question.questionId,
                                  selected
                                    ? current.filter(
                                        item =>
                                          item !==
                                          option
                                      )
                                    : [
                                        ...current,
                                        option
                                      ]
                                );
                              }}
                            />

                            <span>
                              {option}
                            </span>
                          </label>
                        );
                      }
                    )}
                  </div>
                )}

                {question.questionType ===
                  'ranking' && (
                  <>
                    <div className="hp-selection-note">
                      This Poll will select{' '}
                      <strong>
                        {question.selectionCount}
                      </strong>{' '}
                      option{
                        Number(
                          question.selectionCount
                        ) === 1
                          ? ''
                          : 's'
                      }.
                    </div>

                    <RankingAnswer
                      question={
                        question
                      }
                      value={
                        value
                      }
                      onChange={
                        next =>
                          setAnswer(
                            question.questionId,
                            next
                          )
                      }
                    />
                  </>
                )}

                {question.questionType ===
                  'open_text' && (
                  <textarea
                    rows={5}
                    value={
                      value || ''
                    }
                    onChange={
                      event =>
                        setAnswer(
                          question.questionId,
                          event.target.value
                        )
                    }
                  />
                )}
              </section>
            );
          }
        )}

        <button
          className="hp-submit"
          disabled={
            submitting
          }
        >
          {submitting
            ? 'Submitting…'
            : 'Submit Poll'}
        </button>
      </form>
    </div>
  );
}

const pollCss = `
  .hp-shell {
    max-width: 760px;
    margin: 0 auto;
    background: #fff;
    border: 1px solid #d9e2ec;
    border-radius: 8px;
    padding: 24px;
    color: #243b53;
  }

  .hp-shell.compact {
    margin: 0;
    max-width: none;
    border: 0;
    border-radius: 0;
    padding: 18px 16px 90px;
  }

  .hp-heading {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    align-items: flex-start;
    border-bottom: 1px solid #edf1f5;
    padding-bottom: 18px;
    margin-bottom: 18px;
  }

  .hp-heading h2 {
    margin: 4px 0 6px;
    font-size: 24px;
    color: #102a43;
  }

  .hp-heading p {
    margin: 0;
    color: #627d98;
    line-height: 1.55;
    white-space: pre-wrap;
  }

  .hp-kicker {
    text-transform: uppercase;
    letter-spacing: .1em;
    font-size: 10px;
    font-weight: 800;
    color: #627d98;
  }

  .hp-close {
    border: 1px solid #bcccdc;
    background: #fff;
    border-radius: 5px;
    padding: 8px 12px;
    color: #334e68;
    cursor: pointer;
  }

  .hp-error {
    background: #fff3f3;
    border: 1px solid #f3b8b8;
    color: #8a1c1c;
    border-radius: 5px;
    padding: 10px 12px;
    margin-bottom: 14px;
  }

  .hp-question {
    padding: 18px 0;
    border-bottom: 1px solid #edf1f5;
  }

  .hp-question-title {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 12px;
  }

  .hp-question-title strong {
    line-height: 1.4;
  }

  .hp-question-title em {
    margin-left: auto;
    font-size: 10px;
    font-style: normal;
    color: #7b8794;
    text-transform: uppercase;
    letter-spacing: .08em;
  }

  .hp-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .hp-options label {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 9px 10px;
    background: #f7f9fb;
    border: 1px solid #e6ecf1;
    border-radius: 5px;
  }

  .hp-options input {
    margin-top: 2px;
  }

  .hp-question textarea {
    width: 100%;
    resize: vertical;
    border: 1px solid #bcccdc;
    border-radius: 5px;
    padding: 10px;
    font: inherit;
    color: inherit;
  }

  .hp-selection-note,
  .hp-rank-help {
    font-size: 12px;
    color: #627d98;
    margin-bottom: 10px;
  }

  .hp-ranked-list,
  .hp-add-ranking {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .hp-ranked-row {
    display: grid;
    grid-template-columns: 28px minmax(0,1fr) auto auto auto;
    gap: 6px;
    align-items: center;
    padding: 8px;
    border: 1px solid #d9e2ec;
    border-radius: 5px;
  }

  .hp-rank-number {
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    background: #102a43;
    color: #fff;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 800;
  }

  .hp-rank-text {
    min-width: 0;
  }

  .hp-ranked-row button,
  .hp-add-ranking button {
    border: 1px solid #bcccdc;
    background: #fff;
    color: #334e68;
    border-radius: 4px;
    padding: 6px 8px;
    cursor: pointer;
  }

  .hp-ranked-row button:disabled {
    opacity: .4;
  }

  .hp-add-ranking {
    margin-top: 8px;
  }

  .hp-add-ranking button {
    text-align: left;
  }

  .hp-submit {
    width: 100%;
    margin-top: 22px;
    padding: 12px 16px;
    border: 0;
    border-radius: 5px;
    background: #102a43;
    color: white;
    font-weight: 800;
    cursor: pointer;
  }

  .hp-submit:disabled {
    opacity: .6;
    cursor: default;
  }

  @media (max-width: 620px) {
    .hp-ranked-row {
      grid-template-columns: 28px minmax(0,1fr);
    }

    .hp-ranked-row button {
      grid-column: auto;
    }
  }
`;
